import type {
  GetProduct1Data,
  GetProductData,
  GetProductExpectedAmountData,
  GetProductExpectedAmountParams,
  GetProductParams,
  Pageable,
} from '~/api-spec/__generated__/pod'
import { httpClient } from '~/http/client'
import type { Slice } from '~/schema/slice'

import {
  adaptKmmProducts,
  adaptPrintDocumentExpectedAmount,
  adaptPrintDocumentProducts,
  adaptSignatureWorkbookProductDetail,
  adaptSignatureWorkbookProducts,
} from './adapters'
import type {
  KmmProduct,
  PrintDocumentExpectedAmount,
  PrintDocumentProduct,
  SignatureWorkbookProduct,
  SignatureWorkbookProductDetail,
} from './schemas'

// 생성 타입은 Spring Pageable 을 `pageable` 객체로 그리지만 실제 쿼리는 page·size·sort 를 평탄하게 받는다.
// 상품 종류(type)는 요청 함수가 정하므로 호출부가 넘기지 않는다.
type ProductListParams<Key extends keyof GetProductParams> = Pick<GetProductParams, Key> & Pageable

export type SignatureWorkbookProductListParams = ProductListParams<
  'trieKeys' | 'revision' | 'schoolType' | 'grade' | 'semester' | 'statuses' | 'signatureType'
>
export type PrintDocumentProductListParams = ProductListParams<'statuses'>
export type KmmProductListParams = ProductListParams<'round' | 'statuses'>
export type PrintDocumentExpectedAmountParams = Omit<
  GetProductExpectedAmountParams,
  'type' | 'productId'
>

export const getSignatureWorkbookProducts = async (
  params: SignatureWorkbookProductListParams,
): Promise<Slice<SignatureWorkbookProduct>> => {
  const body = await httpClient('pod').get<GetProductData>('/product', {
    params: { ...params, type: 'SIGNATURE_WORKBOOK' },
  })
  return adaptSignatureWorkbookProducts(body)
}

export const getPrintDocumentProducts = async (
  params: PrintDocumentProductListParams,
): Promise<Slice<PrintDocumentProduct>> => {
  const body = await httpClient('pod').get<GetProductData>('/product', {
    params: { ...params, type: 'PRINT_DOCUMENT' },
  })
  return adaptPrintDocumentProducts(body)
}

export const getKmmProducts = async (params: KmmProductListParams): Promise<Slice<KmmProduct>> => {
  const body = await httpClient('pod').get<GetProductData>('/product', {
    params: { ...params, type: 'KMM_AWARDS' },
  })
  return adaptKmmProducts(body)
}

export const getSignatureWorkbookProductDetail = async (
  id: SignatureWorkbookProduct['id'],
): Promise<SignatureWorkbookProductDetail> => {
  const body = await httpClient('pod').get<GetProduct1Data>(`/product/${id}`, {
    params: { type: 'SIGNATURE_WORKBOOK' },
  })
  return adaptSignatureWorkbookProductDetail(body)
}

export const getPrintDocumentExpectedAmount = async (
  id: PrintDocumentProduct['id'],
  params: PrintDocumentExpectedAmountParams,
): Promise<PrintDocumentExpectedAmount> => {
  const body = await httpClient('pod').get<GetProductExpectedAmountData>(
    `/product/${id}/expected-amount`,
    { params: { ...params, type: 'PRINT_DOCUMENT' } },
  )
  return adaptPrintDocumentExpectedAmount(body)
}
